/**
 * 검증된 라이브러리를 사용한 XSS 방어 유틸리티
 */

import DOMPurify from 'dompurify';

/**
 * 위험한 패턴 감지
 * @param input 입력 문자열
 * @returns 위험한 패턴 감지 여부
 */
export const detectMaliciousPatterns = (input: string): boolean => {
    if (!input || typeof input !== 'string') return false;
    
    const dangerousPatterns = [
        /<script/i,
        /<\/script>/i,
        /javascript:/i,
        /on\w+\s*=/i,  // onclick, onload, onerror 등
        /<iframe/i,
        /<object/i,
        /<embed/i,
        /eval\s*\(/i,
        /alert\s*\(/i,
        /document\./i,
        /window\./i,
        /location\./i,
        /<img[^>]+src[^>]*=.*javascript:/i,
        /data:text\/html/i,
        /vbscript:/i,
        /<meta[^>]+http-equiv/i
    ];
    
    return dangerousPatterns.some(pattern => pattern.test(input));
};

/**
 * 위험한 패턴을 제거
 * @param input 입력 문자열
 * @returns 위험한 패턴이 제거된 문자열
 */
export const removeMaliciousPatterns = (input: string): string => {
    // 입력값 안전성 검사
    if (input === null || input === undefined) return '';
    if (typeof input !== 'string') return String(input || '');
    
    let cleaned = String(input);
    
    try {
        // 위험한 패턴들을 순차적으로 제거
        const dangerousPatterns = [
            { pattern: /<script[^>]*>[\s\S]*?<\/script>/gi, replacement: '' },
            { pattern: /<script[^>]*>/gi, replacement: '' },
            { pattern: /<\/script>/gi, replacement: '' },
            { pattern: /javascript:/gi, replacement: '' },
            { pattern: /on\w+\s*=\s*["'][^"']*["']/gi, replacement: '' },
            { pattern: /on\w+\s*=\s*[^"'\s>]+/gi, replacement: '' },
            { pattern: /<iframe[^>]*>[\s\S]*?<\/iframe>/gi, replacement: '' },
            { pattern: /<object[^>]*>[\s\S]*?<\/object>/gi, replacement: '' },
            { pattern: /<embed[^>]*>/gi, replacement: '' },
            { pattern: /eval\s*\([^)]*\)/gi, replacement: '' },
            { pattern: /alert\s*\([^)]*\)/gi, replacement: '' },
            { pattern: /document\.[a-zA-Z_$][a-zA-Z0-9_$]*/gi, replacement: '' },
            { pattern: /window\.[a-zA-Z_$][a-zA-Z0-9_$]*/gi, replacement: '' },
            { pattern: /location\.[a-zA-Z_$][a-zA-Z0-9_$]*/gi, replacement: '' },
            { pattern: /data:text\/html[^"'\s>]*/gi, replacement: '' },
            { pattern: /vbscript:/gi, replacement: '' },
            { pattern: /<meta[^>]+http-equiv[^>]*>/gi, replacement: '' }
        ];
        
        dangerousPatterns.forEach(({ pattern, replacement }) => {
            try {
                cleaned = cleaned.replace(pattern, replacement);
                // null 체크
                if (cleaned === null || cleaned === undefined) {
                    cleaned = '';
                }
            } catch (patternError) {
                console.warn('Pattern replacement error:', patternError);
            }
        });
        
        // 연속된 공백을 단일 공백으로 정리
        cleaned = String(cleaned || '').replace(/\s+/g, ' ').trim();
        
    } catch (error) {
        console.warn('Malicious pattern removal error:', error);
        // 오류 시 최소한의 처리
        cleaned = String(input || '');
    }
    
    return cleaned;
};

/**
 * DOMPurify를 사용한 안전한 HTML 정화
 * @param input 입력 문자열
 * @param allowedTags 허용할 HTML 태그들 (기본값: 모든 태그 제거)
 * @returns 정화된 문자열
 */
export const sanitizeHtml = (
    input: string, 
    allowedTags: string[] = []
): string => {
    // 입력값 안전성 검사
    if (input === null || input === undefined) return '';
    if (typeof input !== 'string') return String(input || '');
    if (input.trim() === '') return '';
    
    try {
        const config = allowedTags.length > 0 
            ? { ALLOWED_TAGS: allowedTags }
            : { ALLOWED_TAGS: [] }; // 모든 HTML 태그 제거
        
        const purified = DOMPurify.sanitize(input, config);
        
        // null 체크 및 안전한 문자열 변환
        if (purified === null || purified === undefined) {
            return '';
        }
        
        return String(purified).trim();
    } catch (error) {
        console.warn('DOMPurify sanitization error:', error);
        // 폴백: 기본 HTML 태그 제거
        return String(input || '').replace(/<[^>]*>/g, '').trim();
    }
};

/**
 * 이중 보안 입력 검증 함수
 * @param input 입력 문자열
 * @param type 검증 타입
 * @param removeThreats 위험 패턴 제거 여부 (기본값: true)
 * @returns 검증된 문자열
 */
export const validateInput = (
    input: string,
    type: 'loginId' | 'nickname' | 'email' | 'referrerCode' | 'realName' | 'password' | 'dateOfBirth' | 'phoneNumber' | 'basic' = 'basic',
    removeThreats: boolean = true
): string => {
    // 입력값 검증 - null, undefined, 빈 문자열 처리
    if (input === null || input === undefined) return '';
    if (typeof input !== 'string') return String(input || '');
    if (input.trim() === '') return '';
    
    let processed = input;
    
    // 1단계: 위험한 패턴 제거 (옵션에 따라)
    if (removeThreats) {
        try {
            processed = removeMaliciousPatterns(processed);
            // 패턴 제거 후 null 체크
            if (processed === null || processed === undefined) {
                processed = '';
            }
        } catch (error) {
            console.warn('Pattern removal error:', error);
            processed = String(input || ''); // 안전한 문자열 변환
        }
    }
    
    // 2단계: HTML 정화 처리
    let sanitized: string = '';
    
    try {
        if (type === 'password') {
            // 비밀번호는 특수문자 보존하며 HTML만 정화
            const purified = DOMPurify.sanitize(processed || '', { 
                ALLOWED_TAGS: [],
                KEEP_CONTENT: true 
            });
            sanitized = String(purified || '').trim();
        } else {
            // 나머지 모든 경우는 기본 HTML 정화
            const htmlCleaned = sanitizeHtml(processed || '');
            sanitized = String(htmlCleaned || '');
        }
    } catch (error) {
        console.warn('Sanitization error:', error);
        // 오류 발생 시 최소한의 안전 처리
        const fallback = String(processed || '').replace(/<[^>]*>/g, '');
        sanitized = fallback.trim();
    }
    
    // 3단계: 정화 후에도 위험 패턴이 남아있으면 한번 더 제거
    if (removeThreats && sanitized && typeof sanitized === 'string') {
        try {
            if (detectMaliciousPatterns(sanitized)) {
                const finalCleaned = removeMaliciousPatterns(sanitized);
                sanitized = String(finalCleaned || '');
            }
        } catch (error) {
            console.warn('Final pattern check error:', error);
        }
    }
    
    // 최종 안전 체크
    return String(sanitized || '');
};

// 하위 호환성을 위한 별칭
export const comprehensiveSecurityFilter = validateInput;